import shared from "../Shared/shared.module.css";
import { IconWallet, IconInfoCircle, IconX, IconArrowRight, IconChevronsLeft } from "@tabler/icons-react";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { priorites } from "../../../constants";
import { talentCompensation, talentProfile } from "../../../services/talentCalls";
import { UserContext } from "../../../context";
import FullScreenLoader from "../../../components/Loaders/FullScreen/loader";
import { currencyFormat } from "../../../utils/functions";
import SubmitResultModal from "../../../components/Onboarding/SubmitResultModal/SubmitResultModal";

const MIN = 0;
const MAX = 10000000;

const Stage6 = () => {
  const { setCount } = useContext(UserContext);
  const [minVal, setMinVal] = useState(0);
  const [maxVal, setMaxVal] = useState(MAX);
  const [arr, setArr] = useState([]);
  const [priorityInput, setPriorityInput] = useState("");
  const [load, setLoad] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [infoOpen, setInfoOpen] = useState(false);
  const [result, setResult] = useState(null); // null | "success" | "failed"
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getPercent = useCallback((value) => Math.round(((value - MIN) / (MAX - MIN)) * 100), []);
  const minPct = getPercent(minVal);
  const maxPct = getPercent(maxVal);

  const submit = async () => {
    try {
      setLoad(true);
      await talentCompensation({
        specificPriorities: arr,
        desiredCompensation: { lowestAmount: minVal, highestAmount: maxVal },
      });
      setCount(2);
      setResult("success");
    } catch (error) {
      setResult("failed");
    } finally {
      setLoad(false);
    }
  };

  const continueToReview = () => {
    setResult(null);
    navigate({ pathname: "/talent-onboarding", search: "stage=7" });
  };

  const contactSupport = () => {
    setResult(null);
    navigate("/talent-support");
  };

  const getUserProfile = async () => {
    setLoading(true);
    try {
      const res = await talentProfile();
      if (res?.data?.data?.talent) setData(res?.data?.data?.talent);
    } catch (_) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUserProfile();
  }, []);

  useEffect(() => {
    if (data?.desiredCompensation?.lowestAmount) setMinVal(Number(data.desiredCompensation.lowestAmount));
    if (data?.desiredCompensation?.highestAmount) setMaxVal(Number(data.desiredCompensation.highestAmount));
    if (data?.specificPriorities) setArr(data.specificPriorities);
  }, [data]);

  const addPriority = (value) => {
    const v = value.trim();
    if (v && !arr.includes(v) && arr.length < 3) setArr((prev) => [...prev, v]);
    setPriorityInput("");
  };

  return (
    <>
      {(loading || load) && <FullScreenLoader />}
      <SubmitResultModal
        isModalOpen={result !== null}
        setModalOpen={(open) => !open && setResult(null)}
        variant={result}
        onPrimary={result === "success" ? continueToReview : submit}
        onSecondary={contactSupport}
        primaryLoading={load}
      />
      <div className={shared.panelHero}>
        <div className={shared.panelHeroLeft}>
          <div className={shared.panelHeroIcon}>
            <IconWallet size={22} />
          </div>
          <div>
            <div className={shared.panelTitle}>Salary Compensation</div>
            <div className={shared.panelSubtitle}>Let us know your salary expectations</div>
          </div>
        </div>
      </div>

      <div className={shared.panelBody}>
        <div className={shared.formField} style={{ marginBottom: 26 }}>
          <div className={shared.sectionLabel} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, position: "relative" }}>
            <span>Desired compensation</span>
            <button
              type="button"
              onClick={() => setInfoOpen((p) => !p)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--amber)", position: "relative" }}
              aria-label="What is this?"
            >
              <IconInfoCircle size={17} />
              {infoOpen && (
                <div
                  style={{
                    position: "absolute", bottom: "calc(100% + 8px)", right: -4, background: "var(--navy)", color: "rgba(255,255,255,0.88)",
                    fontSize: 12, fontWeight: 400, lineHeight: 1.6, padding: "10px 13px", borderRadius: "var(--radius-sm)", width: 220,
                    zIndex: 200, boxShadow: "0 4px 16px rgba(13,27,62,0.22)", textAlign: "left",
                  }}
                >
                  Set the monthly salary range you'd be comfortable accepting for your next role.
                </div>
              )}
            </button>
          </div>

          <div className={shared.rangeDualWrap}>
            <div className={shared.rangeDualTrack} />
            <div className={shared.rangeDualFill} style={{ left: `${minPct}%`, width: `${maxPct - minPct}%` }} />
            <input
              type="range"
              min={MIN}
              max={MAX}
              step={10000}
              value={minVal}
              onChange={(e) => setMinVal(Math.min(+e.target.value, maxVal - 10000))}
            />
            <input
              type="range"
              min={MIN}
              max={MAX}
              step={10000}
              value={maxVal}
              onChange={(e) => setMaxVal(Math.max(+e.target.value, minVal + 10000))}
            />
          </div>
          <div className={shared.rangeDualLabels}>
            <span>₦ 0</span>
            <span style={{ fontWeight: 700, color: "var(--navy)" }}>
              &#8358;{currencyFormat(minVal)} - &#8358;{currencyFormat(maxVal)}
            </span>
            <span>₦ 10m</span>
          </div>
        </div>

        <div className={shared.formField}>
          <div className={shared.sectionLabel}>Do you have any specific priorities?</div>
          <div className={shared.chipInputRow}>
            {arr.map((tag) => (
              <span className={shared.tagEditable} key={tag}>
                {tag}
                <span className={shared.tagRemove} onClick={() => setArr(arr.filter((t) => t !== tag))}>
                  <IconX size={10} />
                </span>
              </span>
            ))}
            {arr.length < 3 && (
              <input
                type="text"
                placeholder="Select 3 priorities for your next job or type and press enter"
                value={priorityInput}
                onChange={(e) => setPriorityInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Tab") {
                    e.preventDefault();
                    addPriority(priorityInput);
                  }
                }}
              />
            )}
          </div>
          {arr.length >= 3 && <div className={shared.chipLimitNote}>Maximum 3 priorities reached</div>}
          <div className="suggestion-row" style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 10 }}>
            {priorites.map((item) => {
              const picked = arr.includes(item);
              return (
                <span
                  key={item}
                  className={shared.tag}
                  style={picked ? { opacity: 0.4, pointerEvents: "none" } : undefined}
                  onClick={() => addPriority(item)}
                >
                  {item}
                </span>
              );
            })}
          </div>
        </div>
      </div>

      <div className={shared.panelFooter}>
        <button type="button" className={shared.btnBack} onClick={() => navigate("?stage=5")}>
          <IconChevronsLeft size={16} /> Back
        </button>
        <div className={shared.footerActions}>
          <button type="button" className={shared.btnPrimary} onClick={submit} disabled={!arr.length || load}>
            Review <IconArrowRight size={15} />
          </button>
        </div>
      </div>
    </>
  );
};

export default Stage6;
